// TelcoTrack — délka vrstvy VYKOP R100 na parcele smlouvy (běžné metry).
// SQL v object-service počítá ST_Length(ST_Intersection) v EPSG:5514 po segmentech;
// tady se délky stejné parcely sečtou a zaokrouhlí na centimetry.

/** Zaokrouhlení průniku na centimetry (běžné metry). Nečíselné / záporné → 0. */
export function roundRouteLengthM(lengthM: number): number {
  if (!Number.isFinite(lengthM) || lengthM < 0) return 0;
  return Math.round(lengthM * 100) / 100;
}

/**
 * Součet délek úseků, které se parcely dotýkají, po klíči parcely.
 * Nula a nečíselné délky se nepřičítají. Výsledek je zaokrouhlený po parcele.
 */
export function sumTouchingSegmentLengths(
  rows: Array<{ key: string; lengthM: number }>,
): Map<string, number> {
  const sums = new Map<string, number>();
  for (const row of rows) {
    const key = row.key.trim();
    if (!key) continue;
    const add = Number.isFinite(row.lengthM) && row.lengthM > 0 ? row.lengthM : 0;
    if (add === 0) continue;
    sums.set(key, (sums.get(key) ?? 0) + add);
  }
  const out = new Map<string, number>();
  for (const [key, sum] of sums) {
    out.set(key, roundRouteLengthM(sum));
  }
  return out;
}

/** Hodnota z API / JSONB. Prázdné → null. Záporné zahodí. */
export function parseR100LengthM(value: unknown): number | null {
  if (value == null || value === '') return null;
  const raw = typeof value === 'number' ? value : Number(String(value).replace(/\s/g, '').replace(',', '.'));
  if (!Number.isFinite(raw) || raw < 0) return null;
  return roundRouteLengthM(raw);
}

/** Krátký český zápis, např. „12,50 m“. Neznámé → pomlčka. */
export function formatR100LengthCs(lengthM: number | null | undefined): string {
  const parsed = parseR100LengthM(lengthM);
  if (parsed == null) return '—';
  const [whole, frac] = parsed.toFixed(2).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${grouped},${frac} m`;
}

export interface R100LengthLabelSource {
  label: string;
  kuNazev?: string | null;
  r100LengthM: number | null;
}

/**
 * Doplní `r100LengthM` podle parcelního čísla (a k.ú., když je vyplněné).
 * Uložená hodnota null nepřepíše číslo, které už na parcele je.
 */
export function assignR100LengthByLabel<
  T extends { parcelniCislo?: string | null; kuNazev?: string | null; r100LengthM?: number | null },
>(parcels: T[], stored: readonly R100LengthLabelSource[]): T[] {
  return parcels.map((parcel) => {
    const label = (parcel.parcelniCislo ?? '').trim();
    if (!label) return parcel;
    const ku = (parcel.kuNazev ?? '').trim();
    const hits = stored.filter((row) => row.label.trim() === label);
    const hit = ku
      ? hits.find((row) => (row.kuNazev ?? '').trim() === ku)
      : hits[0];
    if (!hit || hit.r100LengthM == null) return parcel;
    return { ...parcel, r100LengthM: hit.r100LengthM };
  });
}
